// src/store/contactSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { initialState } from '../../../data/entitlement/contracts/initialState.contracts.data';
import {
  fetchContractsSearchById as fetchContractsSearchByIdAPI,
  fetchAccountingGroupsData as fetchAccountingGroupsAPI,
  fetchCountriesData as fetchCountriesAPI,
  fetchCustomerData as fetchCustomerAPI,
  fetchBillToData as fetchBillToAPI,
  fetchSiteData as fetchSiteAPI,
  fetchStatusData as fetchStatusAPI,
  fetchLevelData as fetchLevelAPI,
  fetchContractsSearch as fetchContractsSearchAPI,
  fetchApprovalsData as fetchApprovalsSearchAPI,
  approvalContract as approvalContractAPI,
  trickleContract as trickleContractAPI,
  saveContractDescription as saveContractDescriptionAPI,
} from '../../../apis/contractsAPI';
import {
  CONTRACTS_DATA,
  handlePending,
  handleRejection,
} from '../../../constants';
import { createAsyncThunkWrapper } from '../../../services/common/asyncThunkWrapper';
import { ACTIONS_CONTRACTS } from '../../../constants/actionCreator';

export const fetchContractsById = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_CONTRACTS_BY_ID,
  fetchContractsSearchByIdAPI
);

export const fetchContracts = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_CONTRACTS,
  fetchContractsSearchAPI
);

export const fetchAccountingGroups = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_ACCOUNTING_GROUPS,
  fetchAccountingGroupsAPI
);

export const fetchCountries = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_COUNTRIES,
  fetchCountriesAPI
);

export const fetchCustomers = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_CUSTOMER,
  fetchCustomerAPI
);

export const fetchBillTos = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_BILLTO,
  fetchBillToAPI
);

export const fetchSites = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_SITE,
  fetchSiteAPI
);

export const fetchStatusData = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_STATUS,
  fetchStatusAPI
);

export const fetchLevelData = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_LEVEL,
  fetchLevelAPI
);

export const fetchApprovalsData = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.FETCH_APPROVALS,
  fetchApprovalsSearchAPI
);

export const approveContractWithNotes = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.APPROVE_NOTES,
  approvalContractAPI
);

export const trickleContract = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.TRICKLE,
  trickleContractAPI
);

export const saveContractDescription = createAsyncThunkWrapper(
  ACTIONS_CONTRACTS.SAVE_CONTRACTS_DESCRIPTION,
  saveContractDescriptionAPI
);

const contractsSlice = createSlice({
  name: 'contracts',
  initialState,
  reducers: {
    handleContractStatus: (state, action) => {
      state.mainScreenContractData.contractStatus = action?.payload;
    },
    isContractsDataFetch: (state, action) => {
      state.isContractsDataFetch = action?.payload;
    },
    resetApprovalGridData: (state) => {
      state.approvalSearchGridData = [];
      state.approvalSearchColData = [];
      state.setSelectedApprovalGridRowsData = [];
      state.approvalGridColumnFilters = {};
    },
    resetContractsSearchGridData: (state) => {
      state.contractsSearchGridData = [];
      state.contractsSearchColData = [];
      state.contractsSearchGridRowSelectedData = [];
      state.filteredData = [];
      state.contractsSearchGridRowSelectedCount = 0;
    },
    setApprovalColData: (state, action) => {
      state.approvalSearchColData = action?.payload ? action.payload : [];
    },
    setContractsSearchFiltersExpanded: (state, action) => {
      state.contractsSearchFiltersExpanded = action?.payload;
    },
    //handle visibility of the Approval Grid
    handleApprovalQueueGridVisibility: (state, action) => {
      state.approvalQueue.isApprovalGridVisible = action?.payload;
    },
    //Navigation Approval Page
    handleApprovalNavigation: (state, action) => {
      state.isContractsApprovalPage = action?.payload;
      state.approvalQueue = initialState.approvalQueue;
    },
    //handle Approval Col Data
    showContractsSearchGrid: (state, action) => {
      state.showContractsSearchGrid = action?.payload;
    },
    // handle Approval Selected Grid Data
    setSelectedApprovalGridRowsData: (state, action) => {
      const additioanlData = {
        ...action.payload,
        contractStatus: state.statusSubmitted,
      };

      state.contractsSearchGridRowSelectedData = action?.payload
        ? action?.payload
        : [];
      state.contractsSearchGridRowSelectedCount = action?.payload
        ? action?.payload.length
        : 0;
      state.mainScreenContractData = additioanlData || [];
      state.isContractsSearchPage = false;
      state.isContractsMainScreen = true;
      state.isContractsApprovalPage = false;
      state.approvalApiResponse = null;
    },
    setSelectedRowApproval: (state, action) => {
      if (action?.payload?.length > 0) {
        state.approvalSelectedRow = action?.payload;
        state.queueArray[state.currentPage ? state.currentPage : 1] =
          action?.payload;
      }
    },

    //Quick Search
    handleQuickSearchId: (state, action) => {
      state.contractsQuickSearchId = action?.payload;
      state.isContractQuikSearch = true;
    },
    setQuickSearchData: (state, action) => {
      state.contractsSearchDatabyId = action?.payload;
      state.isResultsFound = true;
    },
    //Short Name
    handleShortName: (state, action) => {
      state.contractsSearch.shortName = action?.payload;
    },
    //Accounting Modal
    setSelectedAccountGridRowsData: (state, action) => {
      state.contractsSearchModal.gridAccountingRowSelectedData =
        action?.payload?.length > 0
          ? action.payload
          : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearchModal.gridAccountingRowSelectedCount =
        action?.payload?.length > 0 ? action?.payload?.length : 0;
      state.contractsSearch.accountingGroupName = action?.payload[0]?.name
        ? action?.payload[0]?.name
        : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearch.accountingGroupCode = action?.payload[0]
        ?.accountingGroupCode
        ? action?.payload[0]?.accountingGroupCode
        : '';
      state.contractsSearchModal.openAccountingGroupModal = false;
    },
    handleFilteredAccountingGroupData: (state, action) => {
      state.contractsSearchModal.accountingGroupSearchData = action?.payload;
    },
    setAccountingGroupModalOpen: (state) => {
      state.contractsSearchModal.openAccountingGroupModal = true;
      state.contractsSearchModal.accountingGroupSearchForm =
        initialState.contractsSearchModal.accountingGroupSearchForm;
    },
    setAccountingGroupModalClose: (state) => {
      state.contractsSearchModal.openAccountingGroupModal = false;
      state.contractsSearchModal.accountingGroupSearchForm =
        initialState.contractsSearchModal.accountingGroupSearchForm;
    },
    setAccouningGroupFormFeilds: (state, action) => {
      state.contractsSearchModal.accountingGroupSearchForm[
        action?.payload?.name
      ] = action?.payload?.value;
    },
    //Country Modal
    setSelectedCountryGridRowsData: (state, action) => {
      state.contractsSearchModal.gridCountryRowSelectedData =
        action?.payload?.length > 0
          ? action.payload
          : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearchModal.gridCountryRowSelectedCount =
        action?.payload?.length > 0 ? action?.payload?.length : 0;
      state.contractsSearch.countryName = action?.payload[0]?.name
        ? action?.payload[0].name
        : '';
      state.contractsSearch.countryId = action?.payload[0]?.countryId
        ? action?.payload[0].countryId
        : 0;
      state.contractsSearchModal.openCountryModal = false;
    },
    handleFilteredCountryData: (state, action) => {
      state.contractsSearchModal.countrySearchData = action?.payload;
    },
    setCountryModalOpen: (state) => {
      state.contractsSearchModal.openCountryModal = true;
      state.contractsSearchModal.countrySearchForm =
        initialState.contractsSearchModal.countrySearchForm;
    },
    setCountryModalClose: (state) => {
      state.contractsSearchModal.openCountryModal = false;
      state.contractsSearchModal.countrySearchForm =
        initialState.contractsSearchModal.countrySearchForm;
    },
    setCountryFormFeilds: (state, action) => {
      state.contractsSearchModal.countrySearchForm[action?.payload.name] =
        action?.payload?.value;
    },
    //Customer
    setSelectedCustomerGridRowsData: (state, action) => {
      state.contractsSearchModal.gridCustomerRowSelectedData =
        action.payload?.length > 0
          ? action.payload
          : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearchModal.gridCustomerRowSelectedCount =
        action.payload?.length > 0 ? action?.payload?.length : 0;
      state.contractsSearch.customerName = action?.payload[0]?.name
        ? action.payload[0].name
        : '';
      state.contractsSearch.customerId = action?.payload[0]?.customerId
        ? action.payload[0].customerId
        : 0;
      state.contractsSearchModal.openCustomerModal = false;
    },
    handleFilteredCustomerData: (state, action) => {
      state.contractsSearchModal.customerSearchData = action?.payload;
    },
    setCustomerModalOpen: (state) => {
      state.contractsSearchModal.openCustomerModal = true;
      state.contractsSearchModal.customerSearchForm =
        initialState.contractsSearchModal.customerSearchForm;
    },
    setCustomerModalClose: (state) => {
      state.contractsSearchModal.openCustomerModal = false;
      state.contractsSearchModal.customerSearchForm =
        initialState.contractsSearchModal.customerSearchForm;
    },
    setCustomerFormFeilds: (state, action) => {
      state.contractsSearchModal.customerSearchForm[action?.payload?.name] =
        action?.payload?.value;
    },
    //BillTo
    setSelectedBillToGridRowsData: (state, action) => {
      state.contractsSearchModal.gridBillToRowSelectedData =
        action.payload?.length > 0
          ? action.payload
          : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearchModal.gridBillToRowSelectedCount =
        action.payload?.length > 0 ? action?.payload?.length : 0;
      state.contractsSearch.billToName = action?.payload[0]?.name
        ? action.payload[0].name
        : '';
      state.contractsSearch.billToId = action?.payload[0]?.id
        ? action.payload[0].id
        : 0;
      state.contractsSearchModal.openBillToModal = false;
    },
    handleFilteredBillToData: (state, action) => {
      state.contractsSearchModal.billToSearchData = action?.payload;
    },
    setBillToModalOpen: (state) => {
      state.contractsSearchModal.openBillToModal = true;
      state.contractsSearchModal.billToSearchForm =
        initialState.contractsSearchModal.billToSearchForm;
    },
    setBillToModalClose: (state) => {
      state.contractsSearchModal.openBillToModal = false;
      state.contractsSearchModal.billToSearchForm =
        initialState.contractsSearchModal.billToSearchForm;
    },
    setBillToFormFeilds: (state, action) => {
      state.contractsSearchModal.billToSearchForm[action?.payload?.name] =
        action?.payload?.value;
    },
    //Site
    setSelectedSiteGridRowsData: (state, action) => {
      state.contractsSearchModal.gridSiteRowSelectedData =
        action.payload?.length > 0
          ? action.payload
          : CONTRACTS_DATA.inputModalDefaultVal;
      state.contractsSearchModal.gridSiteRowSelectedCount =
        action.payload?.length > 0 ? action?.payload?.length : 0;
      state.contractsSearch.siteName = action?.payload[0]?.name
        ? action.payload[0].name
        : '';
      state.contractsSearch.siteId = action?.payload[0]?.id
        ? action.payload[0].id
        : 0;
      state.contractsSearchModal.openSiteModal = false;
    },
    handleFilteredSiteData: (state, action) => {
      state.contractsSearchModal.siteSearchData = action?.payload;
    },
    setSiteModalOpen: (state) => {
      state.contractsSearchModal.openSiteModal = true;
      state.contractsSearchModal.siteSearchForm =
        initialState.contractsSearchModal.siteSearchForm;
    },
    setSiteModalClose: (state) => {
      state.contractsSearchModal.openSiteModal = false;
      state.contractsSearchModal.siteSearchForm =
        initialState.contractsSearchModal.siteSearchForm;
    },
    setSiteFormFeilds: (state, action) => {
      state.contractsSearchModal.siteSearchForm[action?.payload?.name] =
        action?.payload?.value;
    },
    //Status
    handleStatusFormFields: (state, action) => {
      state.contractsSearch.statusId = action?.payload.value;
    },
    //Level
    handleLevelFormFields: (state, action) => {
      state.contractsSearch.levelId = action?.payload.value;
    },
    //Contracts Grid Data
    setSelectedContractsGridRowsData: (state, action) => {
      state.contractsSearchGridRowSelectedData = action?.payload
        ? action.payload
        : [];
      state.contractsSearchGridRowSelectedCount =
        action.payload?.length > 0 ? action?.payload?.length : 0;
      state.mainScreenContractData = action?.payload ? action.payload : [];
      state.isContractsSearchPage = false;
      state.isContractsMainScreen = action?.payload ? true : false;
      state.isContractsApprovalPage = false;
    },
    //Reset Contracts Search Page Defaults
    handleContractsSearchPageDefaults: (state) => {
      state.showContractsSearchGrid = false;
      state.contractsSearchFiltersExpanded = true;
      state.isContractsSearchPage = true;
      state.contractsSearch = initialState.contractsSearch;
      state.contractsSearchGridData = [];
      state.contractsSearchColData = [];
      state.contractsSearchGridRowSelectedData = [];
      state.contractsSearchGridRowSelectedCount = 0;
    },
    //Reset Contracts Search Form
    handleReset: (state) => {
      state.contractsSearch = initialState.contractsSearch;
      state.contractsSearchGridData = [];
      state.contractsSearchGridTotalRecords = 0;
      state.isAccountingGroupsResultsFound = true;
      state.showContractsSearchGrid = false;
      state.contractsSearchFiltersExpanded = true;
      state.isContractsDataFetch = false;
    },
    //Setup Contracts Search Checkbox Events
    handleNoCountryCheckbox: (state, action) => {
      state.contractsSearch.isNoCountryCheck = action?.payload;
    },
    handleNoCustomerCheckbox: (state, action) => {
      state.contractsSearch.isNoCustomerCheck = action?.payload;
    },
    handleNoBillToCheckbox: (state, action) => {
      state.contractsSearch.isNoBillToCheck = action?.payload;
    },
    handleNoSiteCheckbox: (state, action) => {
      state.contractsSearch.isNoSiteCheck = action?.payload;
    },
    //Errors and Responses
    setValidationError: (state, action) => {
      state.errors = action?.payload;
    },
    setAPISuccessResponse: (state, action) => {
      state.apiSuccessResponse = {
        ...state.apiSuccessResponse,
        ...action.payload,
      };
    },
    setContractsGridTotalRecords: (state, action) => {
      state.contractsSearchGridTotalRecords = action?.payload;
    },
    setApprovalGridFilters: (state, action) => {
      const { columnName, value } = action?.payload || {};
      state.approvalGridColumnFilters[columnName] = value;
    },
    setApprovalGridTotalRecords: (state, action) => {
      state.approvalGridTotalRecords = action?.payload;
    },
    setIsAddNewValue: (state, action) => {
      state.isAddNew = action?.payload;
    },
    setIsMainScreen: (state, action) => {
      state.isMainScreen = action?.payload;
    },
    handleContractSelectedTab: (state, action) => {
      state.isAddNew1 = action?.payload;
    },
    handleManualContract: (state, action) => {
      state.isContractManual = action?.payload;
    },
    //Navigation New Contract Page
    handleAddNewContractNavigation: (state, action) => {
      state.isContractsAddNewPage = action?.payload;
    },
    //Navigation Search Contract Page
    handleSearchContractNavigation: (state, action) => {
      state.isContractsSearchPage = action?.payload;
      state.isContractsDataFetch = false;
    },
    // Navigation Main Screen Automated
    handleMainScreenNavigation: (state, action) => {
      state.isContractsMainScreen = action?.payload;
    },
    //handle Reset to Initial State
    handleResetToInitialState: () => initialState,
    //handle Collapse
    handleCollpaseClick: (state, action) => {
      state.isExpanded = action?.payload;
    },
    //handle Selected Tab
    handleSelectedTab: (state, action) => {
      if (action?.payload !== state.selectededTab) {
        state.prevSelectedTab = state.selectededTab;
      }

      state.selectededTab = action?.payload;
    },
    handleMainScreenContractData: (state, action) => {
      state.mainScreenContractData = action?.payload;
    },
    handleStartDate: (state, action) => {
      state.approvalQueue.startDate = action?.payload;
    },
    handleEndDate: (state, action) => {
      state.approvalQueue.endDate = action?.payload;
    },
    handleStartTime: (state, action) => {
      state.approvalQueue.startTime = action?.payload;
    },
    handleEndTime: (state, action) => {
      state.approvalQueue.endTime = action?.payload;
    },
    handleApprovalQueueResetvalues: (state) => {
      state.approvalQueue = initialState.approvalQueue;
    },
    setApprovalNote: (state, action) => {
      state.approvalQueueNotes = action.payload;
    },
    setApiResponse: (state) => {
      state.approvalApiResponse = null;
    },
    handleAGChange: (state, action) => {
      state.contractsSearch.accountingGroupCode = action?.payload;
      state.contractsSearch.accountingGroupName = action?.payload;
    },
    handleCountryClear: (state) => {
      state.contractsSearch.countryName = '';
      state.contractsSearch.countryId = 0;
    },
    handleCustomerClear: (state) => {
      state.contractsSearch.customerName = '';
      state.contractsSearch.customerId = 0;
    },
    handleContractsTabsLength: (state, action) => {
      state.contractsTabsLength = action?.payload || 0;
    },
    handleQueueArray: (state) => {
      state.queueArray = [];
    },
    handleCurrentPage: (state, action) => {
      state.currentPage = action?.payload;
    },
    handleIsReset: (state, action) => {
      state.isReset = action?.payload;
    },
    handleContractLevel: (state, action) => {
      state.mainScreenContractData['contractLevel'] = action?.payload;
    },
    handlequickIconClicked: (state, action) => {
      state.quickIconClicked = action?.payload;
    },
    handleUpdateUserName: (state, action) => {
      state.mainScreenContractData.creatorFirstName = action?.payload?.firstName;
      state.mainScreenContractData.creatorLastName = action?.payload?.lastName;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContractsById.pending, (state) => {
        state.contractsSearchDatabyId = [];
        state.isContractsByIdResultsFound = null;
        state.isContractLoading = true;
        state.fastFindError = null;
      })
      .addCase(fetchContractsById.fulfilled, (state, action) => {
        state.loading = false;
        if (action?.payload !== null) {
          state.contractsSearchDatabyId = action?.payload ? action.payload : [];
          state.mainScreenContractData = action?.payload ? action.payload : [];
          state.isContractsByIdResultsFound = action?.payload?.length === 0;
          state.isContractsMainScreen = true;
          state.isContractsApprovalPage = false;
          state.isContractsSearchPage = false;
          state.isContractLoading = false;
          state.fastFindError = null;
        } else {
          state.contractsSearchDatabyId = [];
          state.isContractsByIdResultsFound = false;
          state.isContractLoading = false;
          state.fastFindError = true;
        }
      })
      .addCase(fetchContractsById.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchDatabyId = [];
        state.isContractsByIdResultsFound = false;
        state.isContractLoading = false;
        state.fastFindError = true;
      })

      .addCase(fetchContracts.pending, (state) => {
        state.contractsSearchGridData = [];
        state.isContractsSearchResultsFound = null;
        state.contractGridLoading = true;
      })
      .addCase(fetchContracts.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchGridData = action?.payload ? action.payload : [];
        state.contractsSearchGridTotalRecords =
          action.payload?.length > 0 ? action.payload[0].totalRecords : 0;
        state.isContractsSearchResultsFound = action?.payload?.length === 0;
        state.contractGridLoading = false;
      })
      .addCase(fetchContracts.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchGridData = [];
        state.contractsSearchGridTotalRecords = 0;
        state.isContractsSearchResultsFound = true;
        state.contractGridLoading = false;
      })

      .addCase(fetchAccountingGroups.pending, (state) => {
        state.contractsSearchModal.accountingGroupSearchData = [];
        state.isAccountingGroupsResultsFound = null;
      })
      .addCase(fetchAccountingGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchModal.accountingGroups = action?.payload
          ? action?.payload?.accountingGroups
          : [];
        state.contractsSearchModal.accountingGroupSearchData = action?.payload
          ?.accountingGroups
          ? action?.payload?.accountingGroups
          : [];
        state.contractsSearchModal.accountingGroupGridTotalRecords =
          action.payload ? action?.payload?.totalCount : 0;
        state.isAccountingGroupsResultsFound =
          action.payload?.accountingGroups?.length === 0;
      })
      .addCase(fetchAccountingGroups.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchModal.accountingGroupSearchData = [];
        state.contractsSearchModal.accountingGroupGridTotalRecords = 0;
        state.isAccountingGroupsResultsFound = true;
      })

      .addCase(fetchCountries.pending, (state) => {
        state.contractsSearchModal.countrySearchData = [];
        state.isCountryResultsFound = null;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchModal.countries = action?.payload || [];
        state.contractsSearchModal.countrySearchData =
          action?.payload?.country || [];
        state.contractsSearchModal.countryGridTotalRecords = action?.payload
          ? action?.payload?.totalCount
          : 0;
        state.isCountryResultsFound = action?.payload?.length === 0;
      })
      .addCase(fetchCountries.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchModal.countrySearchData = [];
        state.contractsSearchModal.countryGridTotalRecords = 0;
        state.isCountryResultsFound = true;
      })

      .addCase(fetchCustomers.pending, (state) => {
        state.contractsSearchModal.customerSearchData = [];
        state.isCustomersResultsFound = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchModal.customers = action?.payload || [];
        state.contractsSearchModal.customerSearchData = action?.payload || [];
        state.contractsSearchModal.customerGridTotalRecords = action?.payload
          ? action.payload[0].totalRecords
          : 0;
        state.isCustomersResultsFound = action.payload?.length === 0;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchModal.customerSearchData = [];
        state.contractsSearchModal.customerGridTotalRecords = 0;
        state.isCustomersResultsFound = true;
      })

      .addCase(fetchBillTos.pending, (state) => {
        state.contractsSearchModal.billToSearchData = [];
        state.isBillTosResultsFound = null;
      })
      .addCase(fetchBillTos.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchModal.billTos = action?.payload || [];
        state.contractsSearchModal.billToSearchData = action?.payload || [];
        state.contractsSearchModal.billToGridTotalRecords = action?.payload
          ? action.payload[0].totalRecords
          : 0;
        state.isBillTosResultsFound = action?.payload?.length === 0;
      })
      .addCase(fetchBillTos.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchModal.billToSearchData = [];
        state.contractsSearchModal.billToGridTotalRecords = 0;
        state.isBillTosResultsFound = true;
      })

      .addCase(fetchSites.pending, (state) => {
        state.contractsSearchModal.siteSearchData = [];
        state.isSitesResultsFound = null;
      })
      .addCase(fetchSites.fulfilled, (state, action) => {
        state.loading = false;
        state.contractsSearchModal.sites = action?.payload?.sites || [];
        state.contractsSearchModal.siteSearchData = action?.payload?.sites || [];
        state.contractsSearchModal.siteGridTotalRecords = action?.payload?.sites
          ? action?.payload?.sites[0]?.totalRecords
          : 0;
        state.isSitesResultsFound = action.payload?.sites?.length === 0;
      })
      .addCase(fetchSites.rejected, (state, action) => {
        handleRejection(state, action);
        state.contractsSearchModal.siteSearchData = [];
        state.contractsSearchModal.siteGridTotalRecords = 0;
        state.isSitesResultsFound = true;
      })

      .addCase(fetchStatusData.pending, handlePending)
      .addCase(fetchStatusData.fulfilled, (state, action) => {
        state.loading = false;
        state.statusDropDownData = action?.payload || [];
      })
      .addCase(fetchStatusData.rejected, handleRejection)

      .addCase(fetchLevelData.pending, handlePending)
      .addCase(fetchLevelData.fulfilled, (state, action) => {
        state.loading = false;
        state.levelDropDownData = action?.payload;
      })
      .addCase(fetchLevelData.rejected, handleRejection)

      .addCase(fetchApprovalsData.pending, (state) => {
        state.approvalContractLoad = true;
        state.approvalSearchGridData = [];
        state.isApprovalsResultsFound = null;
      })
      .addCase(fetchApprovalsData.fulfilled, (state, action) => {
        state.approvalContractLoad = false;
        state.approvalSearchGridData = action?.payload
          ? action?.payload?.approvalQueue
          : [];
        state.approvalSearchColData = action?.payload
          ? action?.payload?.approvalQueue
          : [];
        state.approvalGridTotalRecords = action?.payload
          ? action?.payload?.totalRecords
          : 0;
        state.isApprovalsResultsFound = action?.payload?.length === 0;
      })
      .addCase(fetchApprovalsData.rejected, (state, action) => {
        handleRejection(state, action);
        state.approvalContractLoad = false;
        state.approvalSearchGridData = [];
        state.approvalGridTotalRecords = 0;
        state.isApprovalsResultsFound = true;
      })
      .addCase(approveContractWithNotes.pending, (state) => {
        state.trickleLoad = true;
        state.approvalApiResponse = null;
        state.contractsForTrickleDown = [];
      })
      .addCase(approveContractWithNotes.fulfilled, (state, action) => {
        state.trickleLoad = false;
        state.approvalApiResponse = true;
        state.contractsForTrickleDown = action?.payload || [];
      })
      .addCase(approveContractWithNotes.rejected, (state) => {
        state.trickleLoad = false;
        state.approvalApiResponse = false;
        state.contractsForTrickleDown = [];
      })

      .addCase(trickleContract.pending, (state) => {
        state.trickleApiResponse = null;
        state.trickleLoading = false;
        state.trickleData = [];
      })
      .addCase(trickleContract.fulfilled, (state, action) => {
        state.trickleApiResponse = true;
        state.trickleLoading = false;
        state.trickleData = action?.payload;
      })
      .addCase(trickleContract.rejected, (state) => {
        state.trickleApiResponse = false;
        state.trickleLoading = false;
        state.trickleData = [];
      })

      .addCase(saveContractDescription.pending, (state) => {
        state.isDescrptionUpdating = true;
      })
      .addCase(saveContractDescription.fulfilled, (state, action) => {
        state.isDescrptionUpdating = false;
        state.description = action?.payload?.description || '';
        state.mainScreenContractData.description = action?.payload?.description;
      })
      .addCase(saveContractDescription.rejected, (state) => {
        state.isDescrptionUpdating = false;
      });
  },
});

export const {
  resetApprovalGridData,
  resetContractsSearchGridData,
  setApprovalColData,
  setContractsSearchFiltersExpanded,
  handleApprovalNavigation,
  showContractsSearchGrid,
  setSelectedApprovalGridRowsData,
  handleQuickSearchId,
  setQuickSearchData,
  handleShortName,
  setSelectedAccountGridRowsData,
  handleFilteredAccountingGroupData,
  setAccountingGroupModalOpen,
  setAccountingGroupModalClose,
  setAccouningGroupFormFeilds,
  setSelectedCountryGridRowsData,
  handleFilteredCountryData,
  setCountryModalOpen,
  setCountryModalClose,
  setCountryFormFeilds,
  setSelectedCustomerGridRowsData,
  handleFilteredCustomerData,
  setCustomerModalOpen,
  setCustomerModalClose,
  setCustomerFormFeilds,
  setSelectedBillToGridRowsData,
  handleFilteredBillToData,
  setBillToModalOpen,
  setBillToModalClose,
  setBillToFormFeilds,
  setSelectedSiteGridRowsData,
  handleFilteredSiteData,
  setSiteModalOpen,
  setSiteModalClose,
  setSiteFormFeilds,
  handleStatusFormFields,
  handleLevelFormFields,
  handleContractsSearchPageDefaults,
  handleReset,
  setValidationError,
  setAPISuccessResponse,
  handleNoCountryCheckbox,
  handleNoCustomerCheckbox,
  handleNoBillToCheckbox,
  handleNoSiteCheckbox,
  setSelectedContractsGridRowsData,
  setApprovalGridFilters,
  setContractsGridTotalRecords,
  setApprovalGridTotalRecords,
  setIsAddNewValue,
  setIsMainScreen,
  handleContractSelectedTab,
  handleManualContract,
  handleAddNewContractNavigation,
  handleSearchContractNavigation,
  handleMainScreenNavigation,
  handleResetToInitialState,
  handleCollpaseClick,
  handleSelectedTab,
  handleMainScreenContractData,
  handleStartDate,
  handleEndDate,
  handleStartTime,
  handleEndTime,
  handleApprovalQueueResetvalues,
  handleApprovalQueueGridVisibility,
  isContractsDataFetch,
  handleContractStatus,
  setApprovalNote,
  setSelectedRowApproval,
  setApiResponse,
  handleAGChange,
  handleCountryClear,
  handleCustomerClear,
  handleContractsTabsLength,
  handleQueueArray,
  handleCurrentPage,
  handleIsReset,
  handleContractLevel,
  handlequickIconClicked,
  handleUpdateUserName,
} = contractsSlice.actions;

export default contractsSlice.reducer;
